<template>
  <div
    class="home-booking-calendar timeline"
    :class="[calendarSizeClass, calendarTextAlignClass, { 'cal-show-time': showGridTime }]"
    :style="calendarRootStyle"
  >
    <div class="cal-top-bar cal-top-bar--compact">
      <div class="calendar-toolbar">
        <button
          type="button"
          class="cal-nav"
          aria-label="Previous month"
          @click="shiftMonth(-1)"
        >
          <span class="cal-nav__icon" aria-hidden="true">‹</span>
        </button>
        <h3 class="cal-title">{{ scrollVisibleTitle }}</h3>
        <button
          type="button"
          class="cal-nav"
          aria-label="Next month"
          @click="shiftMonth(1)"
        >
          <span class="cal-nav__icon" aria-hidden="true">›</span>
        </button>
        <button
          type="button"
          class="cal-today"
          aria-label="Go to today"
          @click="goToday"
        >
          Today
        </button>
        <label class="cal-group-toggle" title="Group rooms by floor">
          <input
            v-model="groupRoomsByFloor"
            type="checkbox"
            class="cal-group-toggle__input"
          />
          <span class="cal-group-toggle__label">Group by floor</span>
        </label>
        <label class="cal-filter cal-filter--room-type">
          <span class="cal-filter__label">Room type</span>
          <select
            v-model="selectedRoomTypeId"
            class="cal-filter__select"
            aria-label="Filter by room type"
          >
            <option
              v-for="opt in roomTypeFilterOptions"
              :key="opt.value === '' ? 'all' : opt.value"
              :value="opt.value"
            >
              {{ opt.text }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <div v-if="loading" class="cal-loading">Loading calendar…</div>

    <div
      v-else
      ref="timelineScroll"
      class="timeline-scroll"
      :style="timelineCssVars"
      @scroll.passive="onTimelineScroll"
    >
      <div class="timeline-sheet">
        <div class="timeline-header">
          <div class="timeline-left timeline-left--header">
            <span class="timeline-header-rooms">Rooms</span>
            <span
              v-if="selectedRoomTypeLabel"
              class="timeline-header-filter"
              :title="`Showing ${selectedRoomTypeLabel} only`"
              >{{ selectedRoomTypeLabel }}</span
            >
          </div>
          <div class="timeline-header__right">
            <div class="timeline-month-band">
              <div
                v-for="seg in monthHeaderSpans"
                :key="seg.key"
                class="timeline-month-band__cell"
                :style="{
                  gridColumn: `${seg.colStart} / span ${seg.colSpan}`,
                }"
              >
                {{ seg.label }}
              </div>
            </div>
            <div class="timeline-days">
              <div
                v-for="h in dayHeaders"
                :key="h.key"
                class="timeline-day"
                :class="{
                  'timeline-day--month-start': h.isMonthStart,
                  'timeline-day--weekend': h.isWeekend,
                  'timeline-day--sunday': h.isSunday,
                  'timeline-day--today': h.isToday,
                }"
              >
                <div class="timeline-day__num">{{ h.day }}</div>
                <div class="timeline-day__wk">{{ h.wk }}</div>
                <div v-if="showGridTime" class="timeline-day__time">12:00</div>
              </div>
            </div>
          </div>
        </div>

        <template v-for="entry in timelineRows">
          <div
            v-if="entry.kind === 'group'"
            :key="entry.key"
            class="timeline-row timeline-row--group"
          >
            <div class="timeline-left timeline-left--group">
              {{ entry.label }}
              <span class="timeline-group-count">({{ entry.count }})</span>
            </div>
            <div class="timeline-track timeline-track--group"></div>
          </div>
          <div
            v-else
            :key="`room-${entry.room.id || entry.room.name}`"
            class="timeline-row"
            :class="{ 'timeline-row--nested': entry.grouped }"
            :style="roomLaneStyle(entry.room)"
            @mouseenter="onRowMouseEnter(entry.room, $event)"
          >
            <div
              class="timeline-left timeline-left--row"
              :class="{ 'timeline-left--nested': entry.grouped }"
              :title="roomRowTitle(entry.room)"
            >
              <span class="timeline-room-name">{{ entry.room.name }}</span>
            </div>
            <div class="timeline-track" :style="roomLaneStyle(entry.room)">
              <div class="timeline-grid">
                <div
                  v-for="cell in halfDayCells"
                  :key="`g-${entry.room.name}-${cell.key}`"
                  class="timeline-cell"
                  :class="[
                    timelineCellClasses(entry.room, cell),
                    cell.isSecondHalf
                      ? 'timeline-cell--second-half'
                      : 'timeline-cell--first-half',
                  ]"
                  @mousedown.prevent="onCellMouseDown(entry.room, cell)"
                  @mouseenter="onCellMouseEnter(entry.room, cell)"
                ></div>
              </div>
              <div class="timeline-bars">
                <div
                  v-for="bar in entry.room.bars"
                  :key="bar.sid"
                  class="booking-bar"
                  :class="bookingBarClasses(bar)"
                  v-tooltip.top-center="bookingTooltipOptions(bar)"
                  @mousedown.stop.prevent="onBarMouseDown(bar, $event)"
                  @click.stop="onBarClick(bar)"
                  :style="bookingBarStyle(bar)"
                >
                  <span class="booking-bar__shape" aria-hidden="true"></span>
                  <span class="booking-bar__text">
                    <template v-if="showGridTime && bar.isStart">{{ barTime(bar) }} </template>{{ bar.label }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="cal-bottom-panel">
      <div class="cal-bottom-card">
        <div
          class="cal-bottom-card__section cal-bottom-card__section--status"
          role="region"
          aria-label="Reservation status legend"
        >
          <h3 class="cal-bottom-card__heading">Reservation status</h3>
          <div
            class="cal-bottom-card__content cal-bottom-card__content--status"
          >
            <div v-if="statusGroups.length" class="cal-status-groups">
              <div
                v-for="(group, groupIndex) in statusGroups"
                :key="'status-group-' + groupIndex"
                class="cal-status-group"
              >
                <div
                  v-for="(row, rowIndex) in group"
                  :key="'status-row-' + groupIndex + '-' + rowIndex"
                  class="cal-status-grid"
                >
                  <span
                    v-for="status in row"
                    :key="status.id"
                    class="cal-status-pill"
                    :style="statusPillStyle(status)"
                    :title="status.name"
                  >
                    {{ status.name }}
                  </span>
                </div>
              </div>
            </div>
            <p v-else class="cal-status-empty">
              No reservation statuses configured
            </p>
          </div>
        </div>
        <div class="cal-bottom-card__section cal-bottom-card__section--reports">
          <slot name="reports"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import axios from '@/plugins/axios';
import { mapState } from 'vuex';
import { getCalendarDisplayMetrics } from '@/utils/calendarDisplaySize';

const STATUS_FAMILIES = [
  [
    'preliminary reservation',
    'confirmed reservation',
    'reservation',
    'reservation + deposit',
    'reservation paid',
    'client did not arrive',
  ],
  [
    'active',
    'stay + deposit',
    'stay paid',
    'stay + overpaid',
    'stay completed',
    'stay unrecorded/unsettled',
  ],
  ['completed unpaid', 'completed overpaid', 'finished'],
];

const LANE_GAP_PX = 4;
const BAR_V_INSET_PX = 3;
const TRACK_PAD_PX = 0;
const TIMELINE_MONTHS_BEFORE = 1;
const TIMELINE_MONTHS_AFTER = 1;
/** Prepend/append more months when scrolling near edges (no fixed end date). */
const EXTEND_CHUNK_MONTHS = 1;

export default {
  name: 'HomeBookingCalendar',
  data() {
    return {
      loading: false,
      month: moment().startOf('month'),
      timelineRangeStartIso: '',
      timelineRangeEndExclusiveIso: '',
      scrollVisibleTitle: '',
      roomByDate: {},
      roomBookings: [],
      bookingStatusLegend: [],
      roomsListRaw: [],
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      _scrollTodayAfterLoad: false,
      _pendingScrollAdjustPx: 0,
      _skipScrollToAnchorAfterLoad: false,
      _extending: false,
      _extendCooldownUntil: 0,
      _scrollRaf: null,
      dragState: null,
      _dragMoved: false,
      groupRoomsByFloor: false,
      selectedRoomTypeId: '',
      allRoomTypes: [],
    };
  },
  mounted() {
    if (!this.setting) {
      this.$store.dispatch('sitesetting/GetSettings').catch(() => {});
    }
    document.addEventListener('mouseup', this.onDocumentMouseUp);
    document.addEventListener('mousemove', this.onDocumentMouseMove);
  },
  computed: {
    ...mapState('sitesetting', ['setting']),
    calendarMetrics() {
      const size =
        this.setting && this.setting.calendar_display_size
          ? this.setting.calendar_display_size
          : 'medium';
      return getCalendarDisplayMetrics(size, this.setting || {});
    },
    calendarSizeClass() {
      return `cal-size-${this.calendarMetrics.key}`;
    },
    statusGroups() {
      const list = Array.isArray(this.bookingStatusLegend)
        ? this.bookingStatusLegend
        : [];
      const byName = {};
      list.forEach((status) => {
        const key = String(status.name || '')
          .trim()
          .toLowerCase();
        if (key) byName[key] = status;
      });
      const used = new Set();
      const groups = STATUS_FAMILIES.map((names) => {
        const items = [];
        names.forEach((name) => {
          const status = byName[name];
          if (!status || used.has(status.id)) return;
          used.add(status.id);
          items.push(status);
        });
        const rows = [];
        for (let i = 0; i < items.length; i += 3) {
          rows.push(items.slice(i, i + 3));
        }
        return rows;
      }).filter((rows) => rows.length);

      const leftover = list.filter((status) => !used.has(status.id));
      if (leftover.length) {
        const rows = [];
        for (let i = 0; i < leftover.length; i += 3) {
          rows.push(leftover.slice(i, i + 3));
        }
        groups.push(rows);
      }
      return groups;
    },
    calendarTextAlignClass() {
      return `cal-text-align-${this.calendarMetrics.textAlign}`;
    },
    showGridTime() {
      const value = this.setting && this.setting.calendar_show_time;
      return !(
        value === false ||
        value === 0 ||
        value === '0' ||
        value === 'false'
      );
    },
    calendarRootStyle() {
      const m = this.calendarMetrics;
      const style = {
        '--day-col': `${m.dayCol}px`,
        '--left-col': `${m.leftCol}px`,
        '--cal-font-scale': String(m.fontScale),
        '--header-day-height': `${m.headerDayHeight || 42}px`,
      };
      return style;
    },
    dayColumnPx() {
      return this.calendarMetrics.dayCol;
    },
    leftColPx() {
      return this.calendarMetrics.leftCol;
    },
    laneBarPx() {
      return this.calendarMetrics.laneBar;
    },
    viewRangeStart() {
      const m = moment(this.timelineRangeStartIso, 'YYYY-MM-DD', true);
      return m.isValid() ? m.startOf('day') : moment().startOf('day');
    },
    viewRangeEndExclusive() {
      const m = moment(this.timelineRangeEndExclusiveIso, 'YYYY-MM-DD', true);
      if (!m.isValid()) {
        return moment()
          .add(1, 'month')
          .startOf('month')
          .startOf('day');
      }
      return m.startOf('day');
    },
    timelineDayCount() {
      const s = this.viewRangeStart;
      const e = this.viewRangeEndExclusive;
      const d = e.diff(s, 'days');
      return Math.max(1, Number.isFinite(d) ? d : 1);
    },
    timelineCssVars() {
      return {
        '--days': String(this.timelineDayCount),
        '--half-cols': String(this.timelineDayCount * 2),
      };
    },
    halfDayCells() {
      const cells = [];
      this.dayHeaders.forEach((h) => {
        cells.push({
          ...h,
          dayKey: h.key,
          key: `${h.key}-am`,
          isSecondHalf: false,
        });
        cells.push({
          ...h,
          dayKey: h.key,
          key: `${h.key}-pm`,
          isSecondHalf: true,
        });
      });
      return cells;
    },
    dayHeaders() {
      const headers = [];
      const start = this.viewRangeStart;
      const today = moment().startOf('day');
      const n = this.timelineDayCount;
      for (let i = 0; i < n; i++) {
        const date = start.clone().add(i, 'days');
        const dow = date.day();
        headers.push({
          key: date.format('YYYY-MM-DD'),
          day: date.date(),
          wk: date.format('dd').toUpperCase(),
          monthShort: date.format('MMM'),
          isMonthStart: date.date() === 1,
          isToday: date.isSame(today, 'day'),
          isSunday: dow === 0,
          isWeekend: dow === 0 || dow === 6,
        });
      }
      return headers;
    },
    monthHeaderSpans() {
      const headers = this.dayHeaders;
      if (!headers.length) return [];
      const spans = [];
      let i = 0;
      while (i < headers.length) {
        const d0 = moment(headers[i].key, 'YYYY-MM-DD', true);
        if (!d0.isValid()) break;
        const y = d0.year();
        const mo = d0.month();
        let span = 0;
        let j = i;
        while (j < headers.length) {
          const d = moment(headers[j].key, 'YYYY-MM-DD', true);
          if (!d.isValid() || d.year() !== y || d.month() !== mo) break;
          span++;
          j++;
        }
        spans.push({
          key: `mb-${headers[i].key}`,
          label: d0.format('MMMM YYYY'),
          colStart: i * 2 + 1,
          colSpan: span * 2,
        });
        i = j;
      }
      return spans;
    },
    totalRoomLabels() {
      return Object.values(this.roomByDate).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0,
      );
    },
    roomTypeFilterOptions() {
      const byId = new Map();
      const addType = (t) => {
        if (!t || t.id == null) return;
        const id = String(t.id);
        if (!byId.has(id)) {
          byId.set(id, t.name || t.code || `Type ${id}`);
        }
      };
      (this.allRoomTypes || []).forEach(addType);
      (this.roomsListRaw || []).forEach((r) => {
        (r.roomTypes || []).forEach(addType);
      });
      const options = [...byId.entries()]
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) =>
          a.text.localeCompare(b.text, undefined, { numeric: true }),
        );
      return [{ value: '', text: 'All room types' }, ...options];
    },
    selectedRoomTypeLabel() {
      const id = this.selectedRoomTypeId;
      if (id == null || String(id) === '') return '';
      const opt = this.roomTypeFilterOptions.find(
        (o) => String(o.value) === String(id),
      );
      return opt && opt.text ? opt.text : '';
    },
    roomsList() {
      const names = (this.roomsListRaw || []).map((r) => ({
        id: r.id,
        name: r.name,
        floorKey:
          r.floor && r.floor.id != null ? String(r.floor.id) : '__none__',
        floorLabel: (r.floor && r.floor.name) || 'Other',
        roomTypes: (r.roomTypes || []).map((t) => ({
          id: t.id,
          name: t.name || t.code || '',
        })),
      }));
      return names
        .map((room) => {
          const row = this._rowWithBars(
            room.name,
            room.id,
            room.floorKey,
            room.floorLabel,
          );
          row.roomTypes = room.roomTypes;
          return row;
        })
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true }),
        );
    },
    filteredRoomsList() {
      const filterId =
        this.selectedRoomTypeId != null &&
        String(this.selectedRoomTypeId) !== ''
          ? String(this.selectedRoomTypeId)
          : '';
      return this.roomsList
        .filter((room) => {
          if (!filterId) return true;
          return (room.roomTypes || []).some((t) => String(t.id) === filterId);
        })
        .map((room) => ({ ...room, typeLabel: '' }));
    },
    timelineRows() {
      const rooms = this.filteredRoomsList;
      if (!this.groupRoomsByFloor) {
        return rooms.map((room) => ({
          kind: 'room',
          room,
          grouped: false,
        }));
      }
      const rows = [];
      const groups = new Map();
      rooms.forEach((room) => {
        const key = room.floorKey || '__none__';
        if (!groups.has(key)) {
          groups.set(key, {
            label: room.floorLabel || 'Other',
            rooms: [],
          });
        }
        groups.get(key).rooms.push(room);
      });
      const sortedKeys = [...groups.keys()].sort((a, b) =>
        groups
          .get(a)
          .label.localeCompare(groups.get(b).label, undefined, {
            numeric: true,
          }),
      );
      sortedKeys.forEach((key) => {
        const group = groups.get(key);
        rows.push({
          kind: 'group',
          key: `floor-${key}`,
          label: group.label,
          count: group.rooms.length,
        });
        group.rooms.forEach((room) => {
          rows.push({ kind: 'room', room, grouped: true });
        });
      });
      return rows;
    },
  },
  watch: {
    '$route.path'(path) {
      if (path === '/') {
        this._scrollTodayAfterLoad = true;
        this.loadMonth();
      }
    },
  },
  created() {
    this.applyAnchorRange(this.month);
    this.scrollVisibleTitle = moment().format('MMMM YYYY');
    this._scrollTodayAfterLoad = true;
    this.loadBookingStatusLegend();
    this.fetchAllRoomTypes();
    this.loadMonth();
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.onDocumentMouseUp);
    document.removeEventListener('mousemove', this.onDocumentMouseMove);
    if (this._scrollRaf != null) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
  },
  methods: {
    async fetchAllRoomTypes() {
      try {
        const res = await axios.get('hms/web/room-types');
        const payload = res.data;
        this.allRoomTypes = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
          ? payload.data
          : [];
      } catch (e) {
        this.allRoomTypes = [];
      }
    },
    barTime(bar) {
      const m = moment(bar && bar.checkInRaw);
      if (m.isValid() && (m.hours() !== 0 || m.minutes() !== 0)) {
        return m.format('HH:mm');
      }
      return '12:00';
    },
    roomRowTitle(room) {
      if (!room) return '';
      if (this.selectedRoomTypeLabel) {
        return `${room.name} — ${this.selectedRoomTypeLabel}`;
      }
      if (room.typeLabel) {
        return `${room.name} — ${room.typeLabel}`;
      }
      const types = (room.roomTypes || []).map((t) => t.name).filter(Boolean);
      if (types.length) {
        return `${room.name} (${types.join(', ')})`;
      }
      return room.name;
    },
    applyAnchorRange(m) {
      const start = m
        .clone()
        .subtract(TIMELINE_MONTHS_BEFORE, 'months')
        .startOf('month')
        .startOf('day');
      const endEx = m
        .clone()
        .add(TIMELINE_MONTHS_AFTER + 1, 'months')
        .startOf('month')
        .startOf('day');
      this.timelineRangeStartIso = start.format('YYYY-MM-DD');
      this.timelineRangeEndExclusiveIso = endEx.format('YYYY-MM-DD');
    },
    shiftMonth(delta) {
      this.month = this.month
        .clone()
        .add(delta, 'month')
        .startOf('month');
      this.applyAnchorRange(this.month);
      this.loadMonth();
    },
    bookingTooltipOptions(bar) {
      return {
        content: (bar && bar.tooltip) || '',
        classes: ['cal-booking-tooltip'],
      };
    },
    goToReservation(bar) {
      const id = bar && bar.reservationId;
      if (!id) return;
      this.$router.push(`/reservation-details/${id}`);
    },
    onBarClick(bar) {
      if (this._dragMoved) {
        this._dragMoved = false;
        return;
      }
      this.goToReservation(bar);
    },
    isBarDragging(bar) {
      return (
        this.dragState &&
        this.dragState.type === 'move' &&
        this.dragState.bar &&
        this.dragState.bar.sid === bar.sid
      );
    },
    timelineCellClasses(room, cell) {
      const roomName = room.name;
      const halfIdx = this.halfSlotIndexForCell(cell);
      const classes = {
        'timeline-cell--month-start': cell.isMonthStart && !cell.isSecondHalf,
        'timeline-cell--weekend': cell.isWeekend,
        'timeline-cell--booked': this.isHalfSlotOccupied(roomName, halfIdx),
        'timeline-cell--selecting': this.isCellInNewSelection(roomName, cell),
        'timeline-cell--move-preview': this.isCellInMovePreview(roomName, cell),
      };
      return classes;
    },
    halfSlotIndexForCell(cell) {
      const day = moment(cell.dayKey, 'YYYY-MM-DD', true).startOf('day');
      if (!day.isValid()) return 0;
      const dayIdx = day.diff(this.viewRangeStart, 'days');
      return dayIdx * 2 + (cell.isSecondHalf ? 1 : 0);
    },
    /** Map drag range to center-line halves (PM check-in → AM check-out). */
    normalizeSelectionHalfRange(minHalf, maxHalf) {
      const lo = Math.min(minHalf, maxHalf);
      const hi = Math.max(minHalf, maxHalf);
      const checkInDayIdx = Math.floor(lo / 2);
      const checkOutDayIdx = Math.floor(hi / 2);
      return {
        startHalf: checkInDayIdx * 2 + 1,
        endHalfEx: checkOutDayIdx * 2 + 1,
      };
    },
    isHalfSlotOccupied(roomName, halfIdx, excludeSid) {
      if (!Number.isFinite(halfIdx)) return false;
      const rangeStart = this.viewRangeStart;
      return (this.roomBookings || []).some((b) => {
        if (b.roomLabel !== roomName) return false;
        if (excludeSid && b.sid === excludeSid) return false;
        const { startHalf, endHalfEx } = this._bookingHalfSlots(
          b,
          roomName,
          rangeStart,
        );
        return halfIdx >= startHalf && halfIdx < endHalfEx;
      });
    },
    isDayOccupied(roomName, dayKey, excludeSid) {
      const day = moment(dayKey, 'YYYY-MM-DD', true).startOf('day');
      if (!day.isValid()) return false;
      return (this.roomBookings || []).some((b) => {
        if (b.roomLabel !== roomName) return false;
        if (excludeSid && b.sid === excludeSid) return false;
        return day.isSameOrAfter(b.start, 'day') && day.isBefore(b.end, 'day');
      });
    },
    isCellInNewSelection(roomName, cell) {
      const d = this.dragState;
      if (!d || d.type !== 'select' || !Array.isArray(d.rooms)) return false;
      if (!d.rooms.some((r) => r.roomName === roomName)) return false;
      if (!Number.isFinite(d.anchorHalf) || !Number.isFinite(d.currentHalf)) {
        return false;
      }
      const halfIdx = this.halfSlotIndexForCell(cell);
      const { startHalf, endHalfEx } = this.normalizeSelectionHalfRange(
        d.anchorHalf,
        d.currentHalf,
      );
      return halfIdx >= startHalf && halfIdx < endHalfEx;
    },
    isCellInMovePreview(roomName, cell) {
      const d = this.dragState;
      if (!d || d.type !== 'move' || !d.preview) return false;
      const previewRoom = d.preview.targetRoomName || d.roomName;
      if (previewRoom !== roomName) return false;
      const { start, endExclusive } = d.preview;
      const rangeStart = this.viewRangeStart;
      const checkOutDay = endExclusive
        .clone()
        .subtract(1, 'day')
        .startOf('day');
      const startHalf = start.diff(rangeStart, 'days') * 2 + 1;
      const endHalfEx = checkOutDay.diff(rangeStart, 'days') * 2 + 1;
      const halfIdx = this.halfSlotIndexForCell(cell);
      return halfIdx >= startHalf && halfIdx < endHalfEx;
    },
    isHalfRangeFreeForRoom(roomName, minHalf, maxHalf, excludeSid) {
      const { startHalf, endHalfEx } = this.normalizeSelectionHalfRange(
        minHalf,
        maxHalf,
      );
      if (startHalf >= endHalfEx) return false;
      for (let h = startHalf; h < endHalfEx; h++) {
        if (this.isHalfSlotOccupied(roomName, h, excludeSid)) {
          return false;
        }
      }
      return true;
    },
    isRangeFreeForRoom(roomName, minKey, maxKey, excludeSid) {
      const startDay = moment(minKey, 'YYYY-MM-DD', true).startOf('day');
      const endDay = moment(maxKey, 'YYYY-MM-DD', true).startOf('day');
      if (!startDay.isValid() || !endDay.isValid()) return false;
      const minHalf = startDay.diff(this.viewRangeStart, 'days') * 2 + 1;
      const maxHalf = endDay.diff(this.viewRangeStart, 'days') * 2;
      return this.isHalfRangeFreeForRoom(
        roomName,
        minHalf,
        maxHalf,
        excludeSid,
      );
    },
    pruneSelectedRooms(d) {
      if (
        !Number.isFinite(d.anchorHalf) ||
        !Number.isFinite(d.currentHalf) ||
        !Array.isArray(d.rooms)
      ) {
        return;
      }
      const minHalf = Math.min(d.anchorHalf, d.currentHalf);
      const maxHalf = Math.max(d.anchorHalf, d.currentHalf);
      d.rooms = d.rooms.filter((r) =>
        this.isHalfRangeFreeForRoom(r.roomName, minHalf, maxHalf),
      );
    },
    addRoomToGroupSelection(room) {
      const d = this.dragState;
      if (!d || d.type !== 'select' || !room) return;
      if (!Array.isArray(d.rooms)) d.rooms = [];
      if (d.rooms.some((r) => r.roomName === room.name)) return;
      if (!Number.isFinite(d.anchorHalf) || !Number.isFinite(d.currentHalf)) {
        return;
      }
      const minHalf = Math.min(d.anchorHalf, d.currentHalf);
      const maxHalf = Math.max(d.anchorHalf, d.currentHalf);
      if (!this.isHalfRangeFreeForRoom(room.name, minHalf, maxHalf)) return;
      d.rooms.push({ roomId: room.id, roomName: room.name });
    },
    onCellMouseDown(room, cell) {
      if (this.loading) return;
      const halfIdx = this.halfSlotIndexForCell(cell);
      if (this.isHalfSlotOccupied(room.name, halfIdx)) {
        this.$fire({
          title: 'Already booked',
          text:
            'This slot is already reserved. Drag across more rooms to group book, or drag a booking to move it.',
          type: 'info',
          timer: 2800,
        });
        return;
      }
      this._dragMoved = false;
      this.dragState = {
        type: 'select',
        anchorHalf: halfIdx,
        currentHalf: halfIdx,
        rooms: [{ roomId: room.id, roomName: room.name }],
      };
    },
    onRowMouseEnter(room, event) {
      const d = this.dragState;
      if (!d) return;
      if (d.type === 'select') {
        this.addRoomToGroupSelection(room);
        return;
      }
      if (d.type === 'move') {
        const target = this.pointerTargetFromEvent(event);
        if (target) {
          this.updateMovePreview(target.dayKey, {
            id: target.roomId,
            name: target.roomName,
          });
        }
      }
    },
    onCellMouseEnter(room, cell) {
      const d = this.dragState;
      if (!d) return;
      if (d.type === 'select') {
        d.currentHalf = this.halfSlotIndexForCell(cell);
        this.pruneSelectedRooms(d);
        this.addRoomToGroupSelection(room);
        return;
      }
      if (d.type === 'move') {
        this.updateMovePreview(cell.dayKey, room);
      }
    },
    onBarMouseDown(bar, event) {
      if (this.loading || !bar || !bar.roomReservationId) return;
      const dayKey = this.dayKeyFromPointer(event);
      this._dragMoved = false;
      const grabDay = dayKey || bar.checkInRaw;
      const grab = moment(grabDay, 'YYYY-MM-DD', true).startOf('day');
      const barStart = moment(bar.checkInRaw).startOf('day');
      const grabOffsetDays = grab.isValid() ? grab.diff(barStart, 'days') : 0;
      this.dragState = {
        type: 'move',
        bar,
        roomName: bar.roomLabel,
        grabOffsetDays,
        anchorDay: grabDay || bar.checkInRaw,
        preview: null,
        original: {
          start: moment(bar.checkInRaw).startOf('day'),
          endExclusive: moment(bar.checkOutRaw)
            .startOf('day')
            .add(1, 'day'),
        },
      };
      this.updateMovePreview(grabDay || bar.checkInRaw, {
        id: bar.roomId,
        name: bar.roomLabel,
      });
    },
    onDocumentMouseMove(event) {
      const d = this.dragState;
      if (!d || d.type !== 'move') return;
      const target = this.pointerTargetFromEvent(event);
      if (target) {
        this.updateMovePreview(target.dayKey, {
          id: target.roomId,
          name: target.roomName,
        });
      }
    },
    pointerTargetFromEvent(event) {
      const row = event.target.closest('.timeline-row');
      const track = row
        ? row.querySelector('.timeline-track')
        : event.target.closest('.timeline-track');
      if (!track || !row) return null;
      const labelEl = row.querySelector('.timeline-left--row');
      const roomName =
        labelEl && labelEl.textContent ? labelEl.textContent.trim() : '';
      const roomRow = (this.roomsList || []).find((r) => r.name === roomName);
      const rawRow = (this.roomsListRaw || []).find((r) => r.name === roomName);
      const rect = track.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const dayIdx = Math.floor(x / this.dayColumnPx);
      if (dayIdx < 0 || dayIdx >= this.timelineDayCount) return null;
      return {
        dayKey: this.viewRangeStart
          .clone()
          .add(dayIdx, 'days')
          .format('YYYY-MM-DD'),
        roomId: (roomRow && roomRow.id) || (rawRow && rawRow.id) || null,
        roomName,
      };
    },
    dayKeyFromPointer(event) {
      const target = this.pointerTargetFromEvent(event);
      return target ? target.dayKey : null;
    },
    updateMovePreview(hoverDayKey, targetRoom) {
      const d = this.dragState;
      if (!d || d.type !== 'move' || !d.original) return;
      const hover = moment(hoverDayKey, 'YYYY-MM-DD', true).startOf('day');
      if (!hover.isValid()) return;
      const newStart = hover
        .clone()
        .subtract(d.grabOffsetDays || 0, 'days')
        .startOf('day');
      const spanDays = d.original.endExclusive.diff(d.original.start, 'days');
      const newEndExclusive = newStart.clone().add(spanDays, 'days');
      const excludeSid = d.bar.bookingSid || d.bar.sidBase;
      const targetRoomName =
        (targetRoom && targetRoom.name) || d.bar.roomLabel || d.roomName;
      const targetRoomId =
        (targetRoom && targetRoom.id) || d.bar.roomId || null;
      if (
        !this.isSpanFree(targetRoomName, newStart, newEndExclusive, excludeSid)
      ) {
        d.preview = null;
        return;
      }
      d.preview = {
        start: newStart,
        endExclusive: newEndExclusive,
        targetRoomId,
        targetRoomName,
      };
      const datesChanged = !newStart.isSame(d.original.start, 'day');
      const roomChanged =
        targetRoomId && d.bar.roomId && targetRoomId !== d.bar.roomId;
      if (datesChanged || roomChanged) {
        this._dragMoved = true;
      }
    },
    isSpanFree(roomName, start, endExclusive, excludeSid) {
      const s = moment(start).startOf('day');
      const e = moment(endExclusive).startOf('day');
      if (!s.isValid() || !e.isValid() || !e.isAfter(s, 'day')) return false;
      const d = s.clone();
      while (d.isBefore(e, 'day')) {
        if (this.isDayOccupied(roomName, d.format('YYYY-MM-DD'), excludeSid)) {
          return false;
        }
        d.add(1, 'day');
      }
      return true;
    },
    selectionRangeKeys(anchorDay, currentDay) {
      const a = moment(anchorDay, 'YYYY-MM-DD', true);
      const b = moment(currentDay, 'YYYY-MM-DD', true);
      if (!a.isValid() || !b.isValid()) return null;
      return {
        min: moment.min(a, b).format('YYYY-MM-DD'),
        max: moment.max(a, b).format('YYYY-MM-DD'),
      };
    },
    datesFromHalfSelection(minHalf, maxHalf) {
      const start = this.viewRangeStart.clone().startOf('day');
      // Selection from center line: check-in on PM half, check-out at next center.
      const checkInDayIdx = Math.floor(minHalf / 2);
      const checkOutDayIdx = Math.floor(maxHalf / 2);
      const checkIn = start
        .clone()
        .add(checkInDayIdx, 'days')
        .format('YYYY-MM-DD');
      const checkOut = start
        .clone()
        .add(checkOutDayIdx, 'days')
        .format('YYYY-MM-DD');
      return { checkIn, checkOut };
    },
    onDocumentMouseUp() {
      const d = this.dragState;
      if (!d) return;
      if (d.type === 'select') {
        this.finishNewBookingSelection(d);
      } else if (d.type === 'move') {
        this.finishMoveBooking(d);
      }
      this.dragState = null;
    },
    finishNewBookingSelection(d) {
      if (!Number.isFinite(d.anchorHalf) || !Number.isFinite(d.currentHalf)) {
        return;
      }
      const minHalf = Math.min(d.anchorHalf, d.currentHalf);
      const maxHalf = Math.max(d.anchorHalf, d.currentHalf);
      const rooms = (d.rooms || []).filter((r) =>
        this.isHalfRangeFreeForRoom(r.roomName, minHalf, maxHalf),
      );
      if (!rooms.length) {
        this.$fire({
          title: 'Cannot book',
          text: 'Selected dates overlap an existing reservation.',
          type: 'error',
          timer: 3000,
        });
        return;
      }
      const { checkIn, checkOut } = this.datesFromHalfSelection(
        minHalf,
        maxHalf,
      );
      this.startBookingForRooms(rooms, checkIn, checkOut);
    },
    async finishMoveBooking(d) {
      if (!d.preview || !d.bar || !d.bar.roomReservationId) {
        this._dragMoved = false;
        return;
      }
      const newCheckIn = d.preview.start.format('YYYY-MM-DD');
      const newCheckOut = d.preview.endExclusive
        .clone()
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      const newRoomId = d.preview.targetRoomId || d.bar.roomId;
      const newRoomName = d.preview.targetRoomName || d.bar.roomLabel;
      const oldRoomName = d.bar.roomLabel || 'Room';
      const oldCheckIn = moment(d.bar.checkInRaw).format('DD/MM/YYYY');
      const oldCheckOut = moment(d.bar.checkOutRaw).format('DD/MM/YYYY');
      const newInLabel = moment(newCheckIn).format('DD/MM/YYYY');
      const newOutLabel = moment(newCheckOut).format('DD/MM/YYYY');
      const datesChanged =
        newCheckIn !== moment(d.bar.checkInRaw).format('YYYY-MM-DD') ||
        newCheckOut !== moment(d.bar.checkOutRaw).format('YYYY-MM-DD');
      const roomChanged = newRoomId && newRoomId !== d.bar.roomId;
      if (!datesChanged && !roomChanged) {
        this._dragMoved = false;
        return;
      }
      let confirmHtml = `Update <strong>${d.bar.label ||
        'booking'}</strong>?<br>`;
      if (roomChanged) {
        confirmHtml += `Room: ${oldRoomName} → ${newRoomName}<br>`;
      }
      if (datesChanged) {
        confirmHtml += `Dates: ${oldCheckIn} – ${oldCheckOut} → ${newInLabel} – ${newOutLabel}`;
      }
      const result = await this.$fire({
        title: roomChanged ? 'Move booking?' : 'Change reservation dates?',
        html: confirmHtml,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, update',
        cancelButtonText: 'Cancel',
      });
      if (!result || !result.value) {
        this._dragMoved = false;
        return;
      }
      try {
        await this.patchRoomReservationDates(
          d.bar,
          newCheckIn,
          newCheckOut,
          newRoomId,
        );
        this.$fire({
          title: 'Updated',
          text: roomChanged
            ? 'Booking moved to the new room.'
            : 'Reservation dates were updated.',
          type: 'success',
          timer: 2500,
        });
        await this.loadMonth();
      } catch (e) {
        this.$fire({
          title: 'Error',
          text: 'Could not update the booking. Please try again.',
          type: 'error',
          timer: 3500,
        });
      }
      this._dragMoved = false;
    },
    resolveRoomTypeId(roomId) {
      const raw = (this.roomsListRaw || []).find((r) => r.id === roomId);
      if (raw && Array.isArray(raw.roomTypes) && raw.roomTypes.length) {
        return raw.roomTypes[0].id;
      }
      return null;
    },
    async resolveRoomTypeIdFromApi(roomId) {
      const cached = this.resolveRoomTypeId(roomId);
      if (cached) return cached;
      const res = await axios.get(`hms/rooms/${roomId}`).catch(() => null);
      const data = res && res.data;
      if (!data) return null;
      if (data.room_type_id && data.room_type_id.room_type) {
        return data.room_type_id.room_type.id;
      }
      if (data.room_type_id && typeof data.room_type_id === 'number') {
        return data.room_type_id;
      }
      if (Array.isArray(data.roomTypes) && data.roomTypes.length) {
        return data.roomTypes[0].id;
      }
      return null;
    },
    async patchRoomReservationDates(bar, checkIn, checkOut, targetRoomId) {
      let roomTypeId = bar.roomTypeId;
      let amenities = bar.amenities;
      let amount = bar.amount;
      let adults = bar.adults;
      let child = bar.child;
      let roomId = targetRoomId || bar.roomId;

      if (!roomTypeId || amenities == null || targetRoomId !== bar.roomId) {
        const res = await axios.get(
          `hms/web/reservations/${bar.reservationId}/rooms/${bar.roomReservationId}`,
        );
        const item = res.data || {};
        amount = item.amount != null ? item.amount : amount;
        adults = item.adults != null ? item.adults : adults;
        child = item.child != null ? item.child : child;
        amenities = item.amenities || amenities || [];
        if (!targetRoomId || targetRoomId === bar.roomId) {
          roomId = (item.room && item.room.id) || roomId;
          roomTypeId =
            (item.roomType && item.roomType.id) ||
            item.room_type_id ||
            roomTypeId;
        }
      }

      if (targetRoomId && targetRoomId !== bar.roomId) {
        roomTypeId = await this.resolveRoomTypeIdFromApi(targetRoomId);
        if (!roomTypeId) {
          throw new Error('Could not resolve room type for target room');
        }
      }

      const amenityIds = Array.isArray(amenities)
        ? amenities.map((a) => (a && a.id != null ? a.id : a))
        : [];

      await axios.patch(
        `hms/web/reservations/${bar.reservationId}/rooms/${bar.roomReservationId}`,
        {
          room_id: roomId,
          room_type_id: roomTypeId,
          amount: amount != null ? amount : 0,
          number_of_room: 1,
          adults: adults != null ? adults : 1,
          child: child != null ? child : 0,
          check_in: checkIn,
          check_out: checkOut,
          amenities: amenityIds,
        },
      );
    },
    async fetchRoomCredential(roomId, checkIn, checkOut) {
      const [roomRes, imagesRes] = await Promise.all([
        axios.get(`hms/rooms/${roomId}`).catch(() => null),
        axios.get(`hms/web/rooms/${roomId}/images`).catch(() => null),
      ]);
      if (!roomRes || !roomRes.data) return null;
      return {
        ...roomRes.data,
        datefilter: { start: checkIn, end: checkOut },
        RoomImages:
          imagesRes && imagesRes.data && imagesRes.data.data
            ? imagesRes.data.data
            : [],
      };
    },
    async startBookingForRooms(rooms, checkIn, checkOut) {
      this.$store.commit('orders/AddFilterDates', {
        start: checkIn,
        end: checkOut,
      });
      if (!rooms || !rooms.length) {
        return;
      }
      try {
        this.$store.commit('orders/updateOrder', []);
        const credentials = await Promise.all(
          rooms.map((r) =>
            this.fetchRoomCredential(r.roomId, checkIn, checkOut),
          ),
        );
        const valid = credentials.filter(Boolean);
        if (!valid.length) {
          this.$fire({
            title: 'Error',
            text: 'Could not load selected rooms. Please try again.',
            type: 'error',
            timer: 3500,
          });
          return;
        }
        valid.forEach((c) => this.$store.commit('orders/addOrders', c));
        const skipped = rooms.length - valid.length;
        if (skipped > 0) {
          this.$fire({
            title: 'Partial load',
            text: `${valid.length} room(s) added. ${skipped} could not be loaded.`,
            type: 'warning',
            timer: 3500,
          });
        }
        this.$router.push('/rooms-booking/');
      } catch (e) {
        this.$fire({
          title: 'Error',
          text: 'Could not start group booking. Please try again.',
          type: 'error',
          timer: 3500,
        });
      }
    },
    goToday() {
      const today = moment().startOf('day');
      this.month = today.clone().startOf('month');
      this.scrollVisibleTitle = this.month.format('MMMM YYYY');
      this.applyAnchorRange(this.month);
      this._skipScrollToAnchorAfterLoad = true;

      const inRange =
        !today.isBefore(this.viewRangeStart, 'day') &&
        today.isBefore(this.viewRangeEndExclusive, 'day');

      if (inRange && !this.loading) {
        this.$nextTick(() => this.scrollToTodayColumn());
        return;
      }

      this._scrollTodayAfterLoad = true;
      this.loadMonth();
    },
    parseBookingStatusList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },

    async loadBookingStatusLegend() {
      try {
        const res = await axios
          .get('hms/web/booking-statuses')
          .catch(() => axios.get('booking-statuses/search?status=active'));
        this.bookingStatusLegend = this.parseBookingStatusList(res.data);
      } catch (e) {
        this.bookingStatusLegend = [];
      }
    },

    statusPillStyle(status) {
      const bg = status && status.color_code ? status.color_code : '#94a3b8';
      return {
        backgroundColor: bg,
        color: this.textColorForBackground(bg),
      };
    },

    textColorForBackground(colorCode) {
      if (!colorCode || typeof colorCode !== 'string') {
        return '#ffffff';
      }
      const hex = colorCode.replace('#', '').trim();
      if (hex.length !== 6) {
        return '#ffffff';
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.55 ? '#000000' : '#ffffff';
    },

    bookingBarClasses(bar) {
      const classes = [];
      if (this.isBarDragging(bar)) {
        classes.push('booking-bar--dragging');
      }
      if (bar.state === 'active') classes.push('booking-bar--active');
      if (bar.state === 'finished') classes.push('booking-bar--finished');
      if (bar.state === 'reservation' || !bar.colorCode) {
        classes.push('booking-bar--reservation');
      }
      const accent = this.barAccentClass(bar);
      if (accent && !bar.colorCode) classes.push(accent);
      if (bar && bar.isStart) classes.push('booking-bar--start');
      if (bar && bar.isEnd) classes.push('booking-bar--end');
      if (bar && bar.isStart && bar.isEnd) {
        classes.push('booking-bar--ribbon');
      } else if (bar && bar.isStart) {
        classes.push(
          bar.startsAfterMidday
            ? 'booking-bar--flat-start'
            : 'booking-bar--chevron-start',
        );
      } else if (bar && bar.isEnd) {
        classes.push(
          bar.endsAtMidday
            ? 'booking-bar--flat-end'
            : 'booking-bar--chevron-end',
        );
      }
      return classes;
    },

    bookingBarCellInsetPx(bar) {
      const halfW = this.dayColumnPx / 2;
      const barW = halfW * Math.max(1, bar.colSpan || 1);
      const chevron = Math.min(12, this.laneBarPx * 0.42);
      return Math.max(2, Math.min(4, Math.round((barW - chevron * 2) * 0.08)));
    },

    bookingBarStyle(bar) {
      const style = {
        gridColumn: `${bar.colStart} / span ${bar.colSpan}`,
        gridRow: `${bar.lane}`,
        '--bar-cell-inset': `${this.bookingBarCellInsetPx(bar)}px`,
      };
      if (bar.colorCode) {
        style['--bar-bg'] = bar.colorCode;
        style['--bar-border'] = bar.colorCode;
        style['--bar-text'] =
          bar.textColor || this.textColorForBackground(bar.colorCode);
      }
      return style;
    },

    barAccentClass(bar) {
      if (!bar || bar.state !== 'reservation') return null;
      const sum = String(bar.sid || '')
        .split('')
        .reduce((a, ch) => a + ch.charCodeAt(0), 0);
      return `booking-bar--tint-${sum % 5}`;
    },
    onTimelineScroll() {
      if (this._scrollRaf != null) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this.updateScrollVisibleTitle();
        this.maybeExtendRange();
      });
    },
    updateScrollVisibleTitle() {
      const el = this.$refs.timelineScroll;
      if (!el || !this.timelineRangeStartIso) return;
      const centerX = el.scrollLeft + el.clientWidth / 2;
      let dayIdx = Math.floor((centerX - this.leftColPx) / this.dayColumnPx);
      const n = this.timelineDayCount;
      if (!Number.isFinite(dayIdx)) dayIdx = 0;
      dayIdx = Math.max(0, Math.min(Math.max(0, n - 1), dayIdx));
      const d = this.viewRangeStart.clone().add(dayIdx, 'days');
      const label = d.format('MMMM YYYY');
      if (label !== this.scrollVisibleTitle) {
        this.scrollVisibleTitle = label;
      }
    },
    maybeExtendRange() {
      const el = this.$refs.timelineScroll;
      if (!el || this.loading || this._extending) return;
      const edge = 200;
      if (el.scrollLeft < edge) {
        this.extendRangeLeft();
      } else if (el.scrollLeft + el.clientWidth >= el.scrollWidth - edge) {
        this.extendRangeRight();
      }
    },
    async extendRangeLeft() {
      if (this._extending || this.loading) return;
      if (Date.now() < this._extendCooldownUntil) return;
      this._extending = true;
      try {
        const oldStart = this.viewRangeStart.clone();
        const newStart = oldStart
          .clone()
          .subtract(EXTEND_CHUNK_MONTHS, 'months')
          .startOf('month')
          .startOf('day');
        const addDays = oldStart.diff(newStart, 'days');
        if (addDays <= 0) return;
        this.timelineRangeStartIso = newStart.format('YYYY-MM-DD');
        this._pendingScrollAdjustPx = addDays * this.dayColumnPx;
        this._skipScrollToAnchorAfterLoad = true;
        await this.loadMonth();
      } finally {
        this._extending = false;
        this._extendCooldownUntil = Date.now() + 450;
      }
    },
    extendRangeRight() {
      if (this._extending || this.loading) return;
      if (Date.now() < this._extendCooldownUntil) return;
      this._extending = true;
      const run = async () => {
        try {
          const oldEndEx = this.viewRangeEndExclusive.clone();
          const newEndEx = oldEndEx
            .clone()
            .add(EXTEND_CHUNK_MONTHS, 'months')
            .startOf('month')
            .startOf('day');
          this.timelineRangeEndExclusiveIso = newEndEx.format('YYYY-MM-DD');
          this._skipScrollToAnchorAfterLoad = true;
          await this.loadMonth();
        } finally {
          this._extending = false;
          this._extendCooldownUntil = Date.now() + 450;
        }
      };
      run();
    },
    scrollToAnchorMonth() {
      this.$nextTick(() => {
        const el = this.$refs.timelineScroll;
        if (!el || typeof el.scrollLeft !== 'number') return;
        const anchor = this.month
          .clone()
          .startOf('month')
          .startOf('day');
        const dayIndex = anchor.diff(this.viewRangeStart, 'days');
        if (dayIndex < 0) return;
        el.scrollLeft = Math.max(0, dayIndex * this.dayColumnPx);
      });
    },
    scrollToTodayColumn() {
      const place = (triesLeft) => {
        const el = this.$refs.timelineScroll;
        if (!el || !el.clientWidth) {
          if (triesLeft > 0) {
            requestAnimationFrame(() => place(triesLeft - 1));
          }
          return;
        }
        const today = moment().startOf('day');
        if (
          today.isBefore(this.viewRangeStart, 'day') ||
          !today.isBefore(this.viewRangeEndExclusive, 'day')
        ) {
          return;
        }
        const idx = today.diff(this.viewRangeStart, 'days');
        el.scrollLeft = Math.max(0, idx * this.dayColumnPx);
        this.updateScrollVisibleTitle();
      };
      this.$nextTick(() => place(8));
    },
    _hasSameDayTurnover(roomName, checkInDay, excludeSid) {
      const inDay = moment(checkInDay).startOf('day');
      if (!inDay.isValid()) return false;
      return (this.roomBookings || []).some((other) => {
        if (other.roomLabel !== roomName) return false;
        if (excludeSid && other.sid === excludeSid) return false;
        const outDay = moment(other.checkOutRaw).startOf('day');
        return outDay.isValid() && outDay.isSame(inDay, 'day');
      });
    },
    _bookingHalfSlots(b, roomName, rangeStart) {
      const checkInDay = moment(b.start).startOf('day');
      const checkOutDay = b.checkOutRaw
        ? moment(b.checkOutRaw).startOf('day')
        : moment(b.end)
            .clone()
            .subtract(1, 'day')
            .startOf('day');
      const checkInIdx = checkInDay.diff(rangeStart, 'days');
      const checkOutIdx = checkOutDay.diff(rangeStart, 'days');

      // Check-in at the day center line (start of PM / second half).
      const startHalf = checkInIdx * 2 + 1;
      // Check-out at the day center line (exclusive end before PM half).
      const endHalfEx = checkOutIdx * 2 + 1;
      return { startHalf, endHalfEx, checkInDay, checkOutDay };
    },
    _segmentHalfSlots(b, roomName, segStart, segEnd, rangeStart) {
      const book = this._bookingHalfSlots(b, roomName, rangeStart);
      const segStartIdx = segStart.diff(rangeStart, 'days');
      const segEndIdx = segEnd.diff(rangeStart, 'days');

      let segStartHalf = segStart.isSame(b.start, 'day')
        ? book.startHalf
        : segStartIdx * 2 + 1;
      let segEndHalfEx = segEnd.isSame(b.end, 'day')
        ? book.endHalfEx
        : Math.max(book.startHalf + 1, segEndIdx * 2 - 1);

      if (segStartHalf >= segEndHalfEx) return null;

      return {
        colStart: segStartHalf + 1,
        colSpan: segEndHalfEx - segStartHalf,
        isStart: segStartHalf === book.startHalf,
        isEnd: segEndHalfEx === book.endHalfEx,
        startsAfterMidday: segStartHalf % 2 === 1,
        endsAtMidday: segEndHalfEx % 2 === 1,
      };
    },
    _packIntoLanes(segments) {
      const lanes = [];
      const bars = [];
      const sorted = segments
        .slice()
        .sort((a, b) => a.colStart - b.colStart || b.colSpan - a.colSpan);
      sorted.forEach((seg) => {
        let laneIndex = 0;
        while (true) {
          if (!lanes[laneIndex]) lanes[laneIndex] = [];
          const overlaps = lanes[laneIndex].some((p) => {
            const a1 = p.colStart;
            const a2 = p.colStart + p.colSpan; // exclusive
            const b1 = seg.colStart;
            const b2 = seg.colStart + seg.colSpan;
            return b1 < a2 && a1 < b2;
          });
          if (!overlaps) {
            lanes[laneIndex].push(seg);
            bars.push({ ...seg, lane: laneIndex + 1 });
            break;
          }
          laneIndex += 1;
        }
      });
      const maxLane = bars.reduce(
        (max, bar) => Math.max(max, bar.lane || 1),
        1,
      );
      return { bars, maxLane };
    },
    roomLaneStyle(room) {
      const lanes = (room && room.maxLane) || 1;
      return {
        '--lane-count': String(lanes),
        '--lane-bar-px': `${this.laneBarPx}px`,
        '--lane-gap-px': `${LANE_GAP_PX}px`,
        '--bar-v-inset': `${BAR_V_INSET_PX}px`,
        '--track-pad-px': `${TRACK_PAD_PX}px`,
      };
    },
    _rowWithBars(name, id, floorKey = '__none__', floorLabel = 'Other') {
      const rangeStart = this.viewRangeStart.clone().startOf('day');
      const rangeEndEx = this.viewRangeEndExclusive.clone().startOf('day');

      const segments = this.roomBookings
        .filter((b) => b.roomLabel === name)
        .map((b) => {
          const segStart = moment.max(b.start, rangeStart);
          const segEnd = moment.min(b.end, rangeEndEx);
          if (!segStart.isBefore(segEnd, 'day')) return null;

          const half = this._segmentHalfSlots(
            b,
            name,
            segStart,
            segEnd,
            rangeStart,
          );
          if (!half || half.colSpan <= 0) return null;

          return {
            sid: `${b.sid}:${rangeStart.format('YYYY-MM-DD')}:${half.colStart}`,
            reservationId: b.reservationId,
            roomReservationId: b.roomReservationId,
            roomId: b.roomId,
            roomTypeId: b.roomTypeId,
            roomLabel: name,
            checkInRaw: b.checkInRaw,
            checkOutRaw: b.checkOutRaw,
            amount: b.amount,
            adults: b.adults,
            child: b.child,
            amenities: b.amenities,
            bookingSid: b.sid,
            sidBase: b.sid,
            state: b.state,
            colorCode: b.colorCode,
            textColor: b.textColor,
            tooltip: b.tooltip,
            label: (b.fullLabel || name).trim(),
            isStart: half.isStart,
            isEnd: half.isEnd,
            startsAfterMidday: half.startsAfterMidday,
            endsAtMidday: half.endsAtMidday,
            colStart: half.colStart,
            colSpan: half.colSpan,
          };
        })
        .filter(Boolean);

      const packed = this._packIntoLanes(segments);
      return {
        id,
        name,
        floorKey,
        floorLabel,
        bars: packed.bars,
        maxLane: packed.maxLane,
      };
    },
    async loadMonth() {
      this.loading = true;
      const rangeStart = this.viewRangeStart.format('YYYY-MM-DD');
      const rangeEnd = this.viewRangeEndExclusive
        .clone()
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      const roomByDate = {};
      const roomBookings = [];

      const pushRoomDay = (dayKey, sid, text, tooltip) => {
        if (!moment(dayKey).isBetween(rangeStart, rangeEnd, 'day', '[]'))
          return;
        if (!roomByDate[dayKey]) roomByDate[dayKey] = [];
        if (roomByDate[dayKey].some((x) => x.sid === sid)) return;
        roomByDate[dayKey].push({ sid, text, tooltip });
      };

      // We treat booking ranges as [start, endExclusive)
      // If reservation status indicates completion, prefer it over dates.
      const bookingStateFor = (checkIn, endExclusive, reservationStatus) => {
        const s = reservationStatus
          ? String(reservationStatus).toLowerCase()
          : '';
        if (s === 'paid' || s === 'completed') return 'finished';
        if (s === 'cancelled') return 'finished';
        const today = moment().startOf('day');
        const start = moment(checkIn).startOf('day');
        const end = moment(endExclusive).startOf('day');
        if (!start.isValid() || !end.isValid()) return 'reservation';
        if (today.isSameOrAfter(end, 'day')) return 'finished';
        if (today.isSameOrAfter(start, 'day') && today.isBefore(end, 'day'))
          return 'active';
        return 'reservation';
      };

      const buildRoomTooltip = (reservation) => {
        const name = (reservation.guest && reservation.guest.name) || 'Guest';
        const status =
          (reservation.booking_status && reservation.booking_status.name) ||
          reservation.status ||
          '';
        const phone = (reservation.guest && reservation.guest.phone) || '';

        return [name, status, phone].filter(Boolean).join('\n');
      };

      try {
        const [resRes, resRooms] = await Promise.all([
          axios.get(
            `hms/web/reservations?start=${rangeStart}&end=${rangeEnd}&page=1&size=2000&calendar=1`,
          ),
          axios.get('hms/web/rooms/all?lite=1'),
        ]);

        const payload = resRes.data;
        const rows = (() => {
          if (Array.isArray(payload)) return payload;
          if (!payload) return [];
          if (Array.isArray(payload.data)) return payload.data;
          if (payload.data && Array.isArray(payload.data.data))
            return payload.data.data;
          if (
            payload.data &&
            payload.data.meta &&
            Array.isArray(payload.data.data)
          )
            return payload.data.data;
          return [];
        })();
        rows.forEach((r) => {
          if (r.status === 'Cancelled') return;

          const guestName =
            (r.guest && r.guest.name && String(r.guest.name).trim()) ||
            (r.guest_name && String(r.guest_name).trim()) ||
            'Guest';

          const roomItems = Array.isArray(r.room_details)
            ? r.room_details
            : Array.isArray(r.rooms)
            ? r.rooms
            : [];

          // Preferred: expand each room booking across check-in -> (check-out - 1 day)
          if (
            roomItems.length &&
            roomItems.some((x) => x && x.check_in && x.check_out)
          ) {
            roomItems.forEach((item, idx) => {
              if (!item || !item.check_in || !item.check_out) return;

              const roomName =
                (item.room && item.room.name) ||
                item.name ||
                (typeof item === 'string' ? item : 'Room');
              const tooltip = buildRoomTooltip(r);
              const sidBase = `res:${r.id ||
                r.reference_number ||
                'x'}:room:${roomName}:${idx}`;

              const start = moment(item.check_in).startOf('day');
              // Make checkout date visible by using an exclusive end (+1 day)
              const endInclusive = moment(item.check_out).startOf('day');
              const endExclusive = endInclusive.clone().add(1, 'day');
              if (!start.isValid() || !endExclusive.isValid()) return;

              const state = bookingStateFor(
                item.check_in,
                endExclusive,
                r.status,
              );

              const bookingStatus = r.booking_status || null;
              const colorCode =
                bookingStatus && bookingStatus.color_code
                  ? bookingStatus.color_code
                  : null;
              // Save booking interval for continuous week bars
              const roomTypeId =
                item.room_type_id ||
                (item.roomType && item.roomType.id) ||
                (item.room &&
                  item.room.room_type_id &&
                  (typeof item.room.room_type_id === 'object'
                    ? item.room.room_type_id.id
                    : item.room.room_type_id)) ||
                null;

              roomBookings.push({
                sid: sidBase,
                reservationId: r.id,
                roomReservationId: item.id,
                roomId: item.room && item.room.id,
                roomTypeId,
                checkInRaw: item.check_in,
                checkOutRaw: item.check_out,
                amount: item.amount,
                adults: item.adults,
                child: item.child,
                amenities: item.amenities,
                state,
                colorCode,
                textColor: colorCode
                  ? this.textColorForBackground(colorCode)
                  : null,
                tooltip,
                roomLabel: `${roomName}`,
                fullLabel: guestName,
                start,
                end: endExclusive,
              });
            });
            return;
          }

          // Fallback 1: reservations list returns rooms as strings (no dates) + single reservation date.
          if (r.date) {
            const key = moment(r.date).format('YYYY-MM-DD');
            const roomNames =
              roomItems.length && typeof roomItems[0] === 'string'
                ? roomItems.join(', ')
                : '';
            const text = roomNames
              ? `${guestName} - ${roomNames}`
              : `${guestName}`;
            const tooltip = buildRoomTooltip(r);
            pushRoomDay(
              key,
              `res:${r.id || r.reference_number || 'x'}:${key}`,
              text,
              tooltip,
            );
          }
        });

        this.roomsListRaw = Array.isArray(resRooms.data) ? resRooms.data : [];
        this.roomByDate = roomByDate;
        this.roomBookings = roomBookings;
      } catch (e) {
        this.roomByDate = {};
        this.roomBookings = [];
        this.roomsListRaw = [];
      } finally {
        this.loading = false;
        const pendingAdjust = this._pendingScrollAdjustPx;
        this._pendingScrollAdjustPx = 0;
        const skipAnchor = this._skipScrollToAnchorAfterLoad;
        this._skipScrollToAnchorAfterLoad = false;
        this.$nextTick(() => {
          const el = this.$refs.timelineScroll;
          if (el && pendingAdjust) {
            el.scrollLeft += pendingAdjust;
          } else if (this._scrollTodayAfterLoad) {
            this._scrollTodayAfterLoad = false;
            this.scrollToTodayColumn();
          } else if (!pendingAdjust && !skipAnchor) {
            this.scrollToAnchorMonth();
          }
          this.updateScrollVisibleTitle();
        });
      }
    },
  },
};
</script>

<style scoped>
.home-booking-calendar {
  width: 100%;
  height: 100%;
  max-width: none;
  margin: 0;
  min-width: 0;
  padding: 0;
  background: #ffffff;
  border-radius: 0;
  border: none;
  box-shadow: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-sizing: border-box;
}

/* Timeline / Gantt — many months in one row; scroll horizontally */
.timeline-scroll {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: auto;
  border-radius: 0;
  border: 0;
  margin: 0;
  scrollbar-gutter: stable;
}

.cal-bottom-panel {
  flex-shrink: 0;
  align-self: stretch;
  width: 100%;
  max-width: none;
  min-width: 0;
  margin-top: auto;
  padding: 8px 12px 0;
}

.cal-bottom-card {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  align-items: stretch;
  gap: 12px;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  overflow: visible;
}

.cal-bottom-card__section {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cal-bottom-card__section--status {
  min-width: 0;
  border-right: 0;
}

.cal-bottom-card__section--reports {
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.cal-bottom-card__section--portal {
  flex: 0 0 340px;
}

.cal-bottom-card__heading {
  margin: 0;
  padding: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  color: #000000;
  text-align: left;
  border: 0;
  background: transparent;
  flex-shrink: 0;
}

.cal-bottom-card__content {
  flex: 1 1 auto;
  display: flex;
  padding: 0;
  min-height: 0;
}

.cal-bottom-card__content--portal {
  align-items: flex-end;
  justify-content: center;
}

.cal-bottom-card__content--status {
  align-items: flex-end;
  justify-content: flex-start;
}

.cal-bottom-card__content--portal >>> .portal-shortcuts--labeled {
  width: 100%;
  align-self: flex-end;
}

.cal-status-groups {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
}

.cal-status-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.cal-status-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 10px;
  width: 100%;
  align-self: stretch;
}

.cal-status-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  clip-path: polygon(
    12px 0%,
    calc(100% - 12px) 0%,
    100% 50%,
    calc(100% - 12px) 100%,
    12px 100%,
    0% 50%
  );
  box-shadow: none;
}

.cal-status-empty {
  margin: 0;
  padding: 8px 4px;
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
  text-align: center;
}

@media (max-width: 900px) {
  .cal-bottom-panel {
    width: 100%;
    max-width: none;
    min-width: 0;
  }

  .cal-bottom-card__section--portal {
    flex-basis: 280px;
  }
}

@media (max-width: 640px) {
  .cal-bottom-card {
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  }

  .cal-bottom-card__section--status {
    min-width: 0;
    border: 0;
  }

  .cal-bottom-card__section--portal {
    flex-basis: auto;
  }

  .cal-bottom-card__content--portal,
  .cal-bottom-card__content--status {
    align-items: stretch;
  }
}

@media (max-width: 520px) {
  .cal-bottom-panel {
    width: 100%;
    min-width: 0;
  }
}

.timeline-sheet {
  display: flex;
  flex-direction: column;
  gap: 0;
  box-sizing: border-box;
  min-width: calc(
    var(--left-col, 220px) + var(--days, 31) * var(--day-col, 56px)
  );
}

.timeline-header {
  position: sticky;
  top: 0;
  z-index: 6;
  display: grid;
  grid-template-columns: var(--left-col, 220px) minmax(0, 1fr);
  align-items: stretch;
  margin: 0;
  padding: 0;
  background: #eef2f6;
  border-bottom: 1px solid #cbd5e1;
  box-shadow: none;
}

.timeline-header__right {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: calc(var(--days) * var(--day-col, 56px));
  background: #f8fafc;
}

.timeline-month-band {
  display: grid;
  grid-template-columns: repeat(
    var(--half-cols),
    calc(var(--day-col, 56px) / 2)
  );
  width: calc(var(--days) * var(--day-col, 56px));
  box-sizing: border-box;
  background: #eef2f6;
  border-bottom: 1px solid #cbd5e1;
}

.timeline-month-band__cell {
  grid-row: 1;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #000000;
  text-transform: capitalize;
  padding: 8px 10px;
  box-sizing: border-box;
  border-left: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-left {
  position: sticky;
  left: 0;
  z-index: 7;
  width: var(--left-col, 220px);
  min-width: var(--left-col, 220px);
  max-width: var(--left-col, 220px);
  padding: 8px 10px;
  box-sizing: border-box;
  color: #000000;
  background: #eef2f6;
  border-right: 1px solid #cbd5e1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timeline-left--header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  align-self: stretch;
  font-weight: 800;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #000000;
  padding: 6px 10px;
  box-sizing: border-box;
  white-space: nowrap;
}

.timeline-header-rooms {
  line-height: 1.2;
}

.timeline-header-filter {
  display: inline-block;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--brand-primary);
  background: var(--brand-primary-fade-12);
  border: 1px solid var(--brand-primary-border);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.timeline-left--row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 0 10px;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  min-height: calc(
    var(--lane-count, 1) * var(--lane-bar-px, 28px) + (var(--lane-count, 1) - 1) *
      var(--lane-gap-px, 1px)
  );
  height: 100%;
  box-sizing: border-box;
}

.timeline-days {
  display: grid;
  grid-template-columns: repeat(
    var(--half-cols),
    calc(var(--day-col, 56px) / 2)
  );
  width: calc(var(--days) * var(--day-col, 56px));
  box-sizing: border-box;
  background: #fff;
}

.timeline-day {
  grid-column: span 2;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 6px 0;
  border-left: 1px solid #e2e8f0;
  border-bottom: 1px solid #cbd5e1;
  color: #000000;
  background: #fff;
  min-height: var(--header-day-height, 42px);
  box-sizing: border-box;
}

.timeline-day--month-start {
  border-left: 1px solid #cbd5e1;
}

.timeline-day--weekend {
  background: #fff5f5;
  color: #000000;
}

.timeline-day--weekend .timeline-day__num,
.timeline-day--weekend .timeline-day__wk {
  color: #000000;
  font-weight: 800;
}

.timeline-day--sunday .timeline-day__num,
.timeline-day--sunday .timeline-day__wk {
  color: #000000;
  font-weight: 800;
}

.timeline-day--today {
  background: linear-gradient(180deg, #fef9c3 0%, #fde68a 100%) !important;
  box-shadow: inset 0 0 0 2px #ca8a04;
  z-index: 1;
}

.timeline-day--today .timeline-day__num,
.timeline-day--today .timeline-day__wk {
  color: #000000;
  font-weight: 900;
}

.timeline-day__num {
  font-weight: 800;
  font-size: 13px;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.timeline-day__wk {
  font-size: 10px;
  font-weight: 700;
  opacity: 1;
  letter-spacing: 0.02em;
}

.timeline-day__time {
  margin-top: 1px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0;
  color: #334155;
}

.cal-show-time .timeline-day {
  min-height: calc(var(--header-day-height, 42px) + 12px);
}

.timeline-row {
  display: grid;
  grid-template-columns: var(--left-col, 220px) minmax(0, 1fr);
  align-items: stretch;
  margin: 0;
  padding: 0;
}

.timeline-row--group {
  background: #eef2f7;
}

.timeline-left--group {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-primary);
  background: #e8edf3;
  border-bottom: 1px solid #cbd5e1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.timeline-group-count {
  margin-left: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #64748b;
}

.timeline-track--group {
  min-height: 28px;
  background: #eef2f7;
  border-bottom: 1px solid #cbd5e1;
}

.timeline-track--group::before {
  opacity: 0.85;
}

.timeline-left--nested {
  padding-left: 22px;
  font-weight: 500;
}

.timeline-row--nested .timeline-left--row {
  background: #fafbfc;
}

.timeline-track {
  position: relative;
  width: calc(var(--days) * var(--day-col, 56px));
  min-width: calc(var(--days) * var(--day-col, 56px));
  padding: 0;
  box-sizing: border-box;
  background: #fff;
  overflow: hidden;
}

.timeline-track::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  z-index: 2;
  background-image: repeating-linear-gradient(
    to right,
    #e2e8f0 0,
    #e2e8f0 1px,
    transparent 1px,
    transparent calc(var(--day-col, 56px) / 2 - 1px),
    #94a3b8 calc(var(--day-col, 56px) / 2 - 1px),
    #94a3b8 calc(var(--day-col, 56px) / 2 + 1px),
    transparent calc(var(--day-col, 56px) / 2 + 1px),
    transparent calc(var(--day-col, 56px) - 1px),
    #e2e8f0 calc(var(--day-col, 56px) - 1px),
    #e2e8f0 var(--day-col, 56px)
  );
  background-size: var(--day-col, 56px) 100%;
}

.timeline-grid {
  display: grid;
  grid-template-columns: repeat(
    var(--half-cols),
    calc(var(--day-col, 56px) / 2)
  );
  gap: 0;
  width: 100%;
  min-height: calc(
    var(--lane-count, 1) * var(--lane-bar-px, 28px) + (var(--lane-count, 1) - 1) *
      var(--lane-gap-px, 1px)
  );
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

.timeline-cell {
  border-bottom: 1px solid #e2e8f0;
  height: 100%;
  min-height: inherit;
  background: transparent;
  box-sizing: border-box;
  cursor: crosshair;
  user-select: none;
}

.timeline-cell--first-half,
.timeline-cell--second-half {
  border-left: none;
}

.timeline-cell--booked {
  cursor: not-allowed;
}

.timeline-cell--selecting {
  background: rgba(59, 130, 246, 0.22) !important;
  box-shadow: inset 0 0 0 1px #3b82f6;
}

.timeline-cell--move-preview {
  background: rgba(234, 179, 8, 0.28) !important;
  box-shadow: inset 0 0 0 1px #ca8a04;
}

.timeline-cell--weekend {
  background: rgba(255, 245, 245, 0.45);
}

.timeline-bars {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: grid;
  grid-template-columns: repeat(
    var(--half-cols),
    calc(var(--day-col, 56px) / 2)
  );
  grid-template-rows: repeat(var(--lane-count, 1), var(--lane-bar-px, 28px));
  row-gap: var(--lane-gap-px, 1px);
  align-content: start;
  pointer-events: none;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  z-index: 3;
  overflow: hidden;
}

.timeline-bars .booking-bar {
  --bar-chevron: min(12px, calc(var(--lane-bar-px, 28px) * 0.42));
  --bar-cell-inset: 3px;
  height: calc(var(--lane-bar-px, 28px) - var(--bar-v-inset, 3px) * 2);
  margin-top: var(--bar-v-inset, 3px);
  margin-bottom: var(--bar-v-inset, 3px);
  box-sizing: border-box;
  pointer-events: auto;
  cursor: grab;
  overflow: hidden;
}

/* Keep ribbon shape; inset within grid cell so tips do not cross column lines */
.booking-bar--ribbon {
  padding-left: var(--bar-cell-inset);
  padding-right: var(--bar-cell-inset);
}

.booking-bar--start.booking-bar--flat-start:not(.booking-bar--ribbon) {
  padding-right: var(--bar-cell-inset);
  padding-left: 0;
}

.booking-bar--end.booking-bar--flat-end:not(.booking-bar--ribbon) {
  padding-left: var(--bar-cell-inset);
  padding-right: 0;
}

.booking-bar--start.booking-bar--chevron-start:not(.booking-bar--end) {
  padding-left: var(--bar-cell-inset);
  padding-right: var(--bar-cell-inset);
}

.booking-bar--end.booking-bar--chevron-end:not(.booking-bar--start) {
  padding-left: var(--bar-cell-inset);
  padding-right: var(--bar-cell-inset);
}

.booking-bar--dragging {
  opacity: 0.45;
  cursor: grabbing;
}

.cal-top-bar {
  --cal-toolbar-control-h: 28px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 6px 12px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 0;
  box-sizing: border-box;
}

.calendar-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.cal-title {
  margin: 0;
  min-width: 0;
  padding: 0 2px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #000000;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.cal-nav {
  width: var(--cal-toolbar-control-h, 28px);
  height: var(--cal-toolbar-control-h, 28px);
  min-width: var(--cal-toolbar-control-h, 28px);
  min-height: var(--cal-toolbar-control-h, 28px);
  padding: 0;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  color: var(--brand-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
}

.cal-nav__icon {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1;
  margin-top: -1px;
  color: #475569;
}

.cal-nav:active {
  transform: translateY(1px);
}

.cal-nav:hover {
  background: #ffffff;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-12);
}

.cal-today {
  margin-left: 6px;
  height: var(--cal-toolbar-control-h, 28px);
  min-height: var(--cal-toolbar-control-h, 28px);
  padding: 0 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 6px;
  background: var(--brand-primary);
  color: #fff !important;
  border: 1px solid var(--brand-primary-hover);
  cursor: pointer;
  box-shadow: 0 1px 3px var(--brand-primary-border);
  line-height: 1;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.cal-today:hover {
  background: var(--brand-primary-hover);
  border-color: var(--brand-primary-hover);
}

.cal-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  height: var(--cal-toolbar-control-h, 28px);
  min-height: var(--cal-toolbar-control-h, 28px);
  padding: 0 8px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;
}

.cal-group-toggle:hover {
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-muted);
}

.cal-group-toggle__input {
  width: 12px;
  height: 12px;
  margin: 0;
  accent-color: var(--brand-primary);
  cursor: pointer;
}

.cal-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;
  height: var(--cal-toolbar-control-h, 28px);
  min-height: var(--cal-toolbar-control-h, 28px);
  padding: 0 8px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  font-size: 11px;
  color: #334155;
  white-space: nowrap;
  box-sizing: border-box;
}

.cal-filter__label {
  font-weight: 600;
  color: #475569;
}

.cal-filter__select {
  min-width: 120px;
  max-width: 180px;
  height: calc(var(--cal-toolbar-control-h, 28px) - 8px);
  padding: 0 6px;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  background: #fff;
  font-size: 11px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  box-sizing: border-box;
}

.cal-filter__select:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 2px var(--brand-primary-fade-12);
}

.timeline-room-name {
  font-weight: 600;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.cal-top-bar__right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-left: auto;
  justify-content: flex-end;
  flex: 1 1 auto;
  min-width: 0;
}

.cal-legend {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 16px 22px;
  flex-shrink: 0;
  padding: 0;
  margin: 0;
  margin-left: 0;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  background: transparent;
  border: none;
}

.cal-portal-icons {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-left: 4px;
  margin-left: 2px;
  border-left: 1px solid #e2e8f0;
}

.cal-portal-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--brand-primary-fade-20);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff !important;
  line-height: 1;
  box-shadow: 0 1px 2px var(--brand-primary-fade-20);
  transition: background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
}

.cal-portal-btn i {
  font-size: 14px;
  margin: 0;
  padding: 0;
  line-height: 1;
}

.cal-portal-btn:hover {
  box-shadow: 0 2px 6px var(--brand-primary-border);
  transform: translateY(-1px);
}

.cal-portal-btn:active {
  transform: scale(0.96) translateY(0);
}

.cal-portal-btn--reservations {
  background: linear-gradient(
    180deg,
    var(--brand-primary) 0%,
    var(--brand-primary-hover) 100%
  );
}

.cal-portal-btn--takeaway {
  background: linear-gradient(
    180deg,
    var(--brand-primary-light) 0%,
    var(--brand-primary) 100%
  );
}

.cal-portal-btn--table {
  background: linear-gradient(
    180deg,
    var(--brand-primary-mid) 0%,
    var(--brand-primary-hover) 100%
  );
}

.cal-portal-btn--tickets {
  background: linear-gradient(
    180deg,
    var(--brand-primary-hover) 0%,
    var(--brand-primary-dark) 100%
  );
}

.cal-legend__label {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  white-space: nowrap;
  padding: 4px 0;
  margin-right: 2px;
}

@media (max-width: 640px) {
  .cal-top-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .cal-top-bar__right {
    width: 100%;
    margin-left: 0;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .cal-legend {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
    padding-top: 4px;
    border-top: 1px solid #f1f5f9;
  }

  .cal-portal-icons {
    width: 100%;
    justify-content: flex-start;
    margin-left: 0;
    padding-left: 0;
    padding-top: 6px;
    border-left: none;
    border-top: 1px solid #f1f5f9;
  }
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.legend-item--muted {
  color: #94a3b8;
  font-size: 11px;
  font-style: italic;
}

.dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.35);
}

.dot.room-reservation {
  background: linear-gradient(180deg, #3b82f6, #2563eb);
  border: 1px solid #1d4ed8;
  box-sizing: border-box;
}

.dot.room-active {
  background: linear-gradient(180deg, #fb923c, #ea580c);
  border: 1px solid #c2410c;
  box-sizing: border-box;
}

.dot.room-finished {
  background: linear-gradient(180deg, #94a3b8, #64748b);
  border: 1px solid #475569;
  box-sizing: border-box;
}

.cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  flex-shrink: 0;
  margin-bottom: 4px;
  font-size: clamp(10px, 1vw, 11px);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #404040;
  text-align: center;
}

.cal-hint {
  flex-shrink: 0;
  margin: 0 0 6px;
  text-align: center;
  font-size: 12px;
  color: #525252;
}

.cal-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(6px, 1vh, 10px);
  flex: 1;
  min-height: 0;
  align-content: stretch;
}

.cal-week {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  min-height: 0;
}

.cal-week-cells {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: clamp(3px, 0.55vw, 8px);
  min-height: 0;
  /* Reserve vertical space for booking bars so they never overlap cells. */
  padding-bottom: calc(var(--lane-count, 1) * 22px + 6px);
}

.cal-week-bars {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 24px;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(var(--lane-count, 1), 22px);
  gap: clamp(3px, 0.55vw, 8px);
  padding: 0;
  align-content: start;
  row-gap: 4px;
  height: calc(var(--lane-count, 1) * 22px + (var(--lane-count, 1) - 1) * 4px);
}

.booking-bar {
  position: relative;
  pointer-events: auto;
  height: calc(var(--lane-bar-px, 28px) - var(--bar-v-inset, 3px) * 2);
  margin-top: var(--bar-v-inset, 3px);
  margin-bottom: var(--bar-v-inset, 3px);
  align-self: start;
  font-size: calc(10px * var(--cal-font-scale, 1));
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: hidden;
  min-width: 0;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.booking-bar__shape {
  position: absolute;
  inset: 0;
  z-index: 0;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: none;
  pointer-events: none;
}

.booking-bar[style*='--bar-bg'] .booking-bar__shape {
  background: var(--bar-bg);
  border-color: var(--bar-border);
}

.booking-bar[style*='--bar-text'] .booking-bar__text {
  color: var(--bar-text);
}

.booking-bar__text {
  position: absolute;
  left: calc(var(--bar-chevron, 10px) + 2px);
  right: auto;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: auto;
  max-width: calc(100% - (var(--bar-chevron, 10px) + 2px) * 2);
  pointer-events: none;
  text-align: left;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.12);
}

.cal-text-align-center .booking-bar__text {
  left: 50%;
  right: auto;
  transform: translate(-50%, -50%);
  text-align: center;
}

.cal-text-align-right .booking-bar__text {
  left: auto;
  right: calc(var(--bar-chevron, 10px) + 2px);
  transform: translateY(-50%);
  text-align: right;
}

.cal-text-align-center .timeline-day {
  align-items: center;
  text-align: center;
}

.cal-text-align-right .timeline-day {
  align-items: flex-end;
  text-align: right;
  padding-right: 6px;
}

.cal-text-align-left .timeline-day {
  align-items: flex-start;
  text-align: left;
  padding-left: 6px;
}

.booking-bar--reservation .booking-bar__shape {
  background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);
  border-color: #1d4ed8;
}

.booking-bar--reservation.booking-bar--tint-0 .booking-bar__shape {
  background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);
  border-color: #1d4ed8;
}

.booking-bar--reservation.booking-bar--tint-1 .booking-bar__shape {
  background: linear-gradient(180deg, #4ade80 0%, #16a34a 100%);
  border-color: #15803d;
}

.booking-bar--reservation.booking-bar--tint-2 .booking-bar__shape {
  background: linear-gradient(180deg, #a78bfa 0%, #7c3aed 100%);
  border-color: #6d28d9;
}

.booking-bar--reservation.booking-bar--tint-3 .booking-bar__shape {
  background: linear-gradient(180deg, #fbbf24 0%, #d97706 100%);
  border-color: #b45309;
}

.booking-bar--reservation.booking-bar--tint-3 .booking-bar__text {
  color: #422006;
  text-shadow: none;
}

.booking-bar--reservation.booking-bar--tint-4 .booking-bar__shape {
  background: linear-gradient(180deg, #fb7185 0%, #e11d48 100%);
  border-color: #be123c;
}

.booking-bar--active .booking-bar__shape {
  background: linear-gradient(180deg, #fdba74 0%, #ea580c 100%);
  border-color: #c2410c;
}

.booking-bar--active .booking-bar__text {
  color: #431407;
  text-shadow: none;
}

.booking-bar--finished .booking-bar__shape {
  background: linear-gradient(180deg, #cbd5e1 0%, #64748b 100%);
  border-color: #475569;
}

.booking-bar--finished .booking-bar__text {
  color: #f1f5f9;
}

.booking-bar--reservation .booking-bar__text,
.booking-bar:not([style*='--bar-text']) .booking-bar__text {
  color: #fff;
}

/* Ribbon pill — pointed ends like reservation status legend */
.booking-bar--ribbon .booking-bar__shape {
  clip-path: polygon(
    var(--bar-chevron, 10px) 0%,
    calc(100% - var(--bar-chevron, 10px)) 0%,
    100% 50%,
    calc(100% - var(--bar-chevron, 10px)) 100%,
    var(--bar-chevron, 10px) 100%,
    0% 50%
  );
}

.booking-bar--start.booking-bar--flat-start:not(.booking-bar--end)
  .booking-bar__shape {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
}

.booking-bar--start.booking-bar--chevron-start:not(.booking-bar--end)
  .booking-bar__shape {
  clip-path: polygon(
    var(--bar-chevron, 10px) 0%,
    100% 0%,
    100% 100%,
    var(--bar-chevron, 10px) 100%,
    0% 50%
  );
}

.booking-bar--end.booking-bar--flat-end:not(.booking-bar--start)
  .booking-bar__shape {
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
}

.booking-bar--end.booking-bar--chevron-end:not(.booking-bar--start)
  .booking-bar__shape {
  clip-path: polygon(
    0% 0%,
    calc(100% - var(--bar-chevron, 10px)) 0%,
    100% 50%,
    calc(100% - var(--bar-chevron, 10px)) 100%,
    0% 100%
  );
}

.booking-bar--start::before,
.booking-bar--end::after {
  display: none;
}

.cal-cell {
  min-height: 0;
  padding: clamp(3px, 0.7vh, 10px);
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #e5e5e5;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.cal-cell--muted {
  opacity: 0.38;
}

.cal-cell--today {
  border-color: #0a0a0a;
  background: #f5f5f5;
}

.cal-daynum {
  font-size: clamp(11px, 1.35vh, 18px);
  font-weight: 600;
  color: #0a0a0a;
  align-self: flex-start;
}

.cal-markers {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-top: 4px;
  justify-content: center;
}

.cal-room-labels {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 0;
}

.room-label {
  border: 1px solid rgba(0, 0, 0, 0.2);
  color: #0a0a0a;
  padding: 3px 6px;
  font-weight: 700;
  font-size: clamp(8px, 1.05vh, 11px);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #d4d4d4;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.room-label--reservation {
  background: #d4d4d4;
  color: #0a0a0a;
}

.room-label--active {
  background: #fafafa;
  color: #000;
}

.room-label--finished {
  background: #e5e5e5;
  color: #0a0a0a;
}

.room-label--start {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.room-label--cont {
  border-radius: 0;
  padding: 2px 6px;
  font-weight: 700;
  font-size: clamp(8px, 0.95vh, 10px);
  opacity: 0.95;
}

.room-label--end {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.room-label--more {
  background: rgba(163, 163, 163, 0.55);
  font-weight: 600;
  color: #0a0a0a;
}

.marker {
  font-size: clamp(8px, 1.1vh, 11px);
  font-weight: 700;
  padding: 2px 5px;
  border-radius: 4px;
  color: #0a0a0a;
  line-height: 1.2;
  cursor: default;
}

.marker.room {
  background: #d4d4d4;
  color: #0a0a0a;
}

.cal-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  min-height: 0;
  background: #f8fafc;
}

.home-booking-calendar {
  --cal-font-scale: 1;
  font-size: calc(14px * var(--cal-font-scale));
}

.cal-size-pos .cal-top-bar {
  --cal-toolbar-control-h: 34px;
}

.cal-size-pos .cal-nav {
  width: var(--cal-toolbar-control-h);
  height: var(--cal-toolbar-control-h);
  min-width: var(--cal-toolbar-control-h);
  min-height: var(--cal-toolbar-control-h);
}

.cal-size-pos .cal-portal-btn {
  min-height: var(--cal-toolbar-control-h, 40px);
  min-width: var(--cal-toolbar-control-h, 40px);
}

.cal-size-pos .cal-title {
  font-size: calc(1.1rem * var(--cal-font-scale));
}

.cal-size-compact .cal-status-pill {
  min-height: 20px;
  font-size: 7px;
}

.cal-size-pos .cal-status-pill {
  min-height: 28px;
  font-size: 10px;
}
</style>

<style>
.tooltip.cal-booking-tooltip .tooltip-inner {
  max-width: 240px;
  padding: 8px 12px;
  text-align: left;
  white-space: pre-line;
  line-height: 1.5;
  font-size: 13px;
  background: #ffffff;
  color: #1e293b;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.12);
}

.tooltip.cal-booking-tooltip .tooltip-arrow {
  width: 0;
  height: 0;
  border-style: solid;
  position: absolute;
  z-index: 1;
}

.tooltip.cal-booking-tooltip[x-placement^='top'] {
  margin-bottom: 8px;
}

.tooltip.cal-booking-tooltip[x-placement^='top'] .tooltip-arrow {
  border-width: 7px 7px 0 7px !important;
  border-top-color: #94a3b8 !important;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-bottom-color: transparent !important;
  bottom: -7px !important;
  left: calc(50% - 7px) !important;
  margin: 0 !important;
}

.tooltip.cal-booking-tooltip[x-placement^='bottom'] {
  margin-top: 8px;
}

.tooltip.cal-booking-tooltip[x-placement^='bottom'] .tooltip-arrow {
  border-width: 0 7px 7px 7px !important;
  border-bottom-color: #94a3b8 !important;
  border-left-color: transparent !important;
  border-right-color: transparent !important;
  border-top-color: transparent !important;
  top: -7px !important;
  left: calc(50% - 7px) !important;
  margin: 0 !important;
}
</style>
